import { Link } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';

/**
 * Phase-9 privacy policy. Plain-language and tied to what the platform
 * actually does today — not a stock template. Lives at `/privacy` and
 * is linked from the AppHeader / landing footer / Settings → Account.
 *
 * The policy will need a real legal pass before launch (Dr. Cato + a
 * lawyer); this is the engineering-truth version that documents what
 * the system does. Update it when behavior changes — particularly
 * before adding any third-party data destinations (Sentry / PostHog
 * / Stripe / external AI providers under direct integration).
 */
export function Privacy() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-[hsl(220,40%,98%)] via-background to-secondary/40">
      <main className="container max-w-3xl py-10">
        <Link
          to="/"
          className="inline-flex items-center gap-1 text-sm font-medium text-muted-foreground hover:text-foreground"
        >
          <ChevronLeft className="h-4 w-4" aria-hidden /> Back
        </Link>

        <header className="mt-3 mb-8 space-y-2">
          <h1 className="font-display text-3xl font-bold tracking-tight">Privacy</h1>
          <p className="text-sm text-muted-foreground">
            What EduBorg collects, how it's used, and what you can do about it.
          </p>
          <p className="text-xs text-muted-foreground">Last updated: 2026-04-29.</p>
        </header>

        <Section title="What we collect">
          <p>
            When you sign up, we store your name, email, and password (hashed — never in plaintext).
          </p>
          <p>
            When you use the platform, we store the work you produce: courses you create or join,
            assessment attempts and answers, discussion posts, direct messages, and any Tutoring
            Center / Study Room conversations.
          </p>
          <p>
            We log AI model calls (which surface, when, how many tokens) for billing and abuse
            prevention. The full prompt + response of those calls is not retained beyond the
            response itself.
          </p>
          <p>
            We log security-relevant events (sign-ins, role changes, payment events, account
            deletions) in an append-only audit log. Required for FERPA / accreditation /
            payment-dispute records.
          </p>
        </Section>

        <Section title="How we use it">
          <p>
            To run the platform: render your courses, grade your assessments, route your messages,
            surface your notifications, and protect against abuse (rate limits, AI credit caps,
            audit trails).
          </p>
          <p>
            We don't sell your data. We don't run advertising on EduBorg. We don't share your data
            with third parties except where strictly required to operate the service — Cloudflare
            hosts the database and Workers; Resend sends email; the AI/ML gateway we use ships
            prompts to the model provider for the response. Each of those transmissions is the
            minimum needed to fulfill what you asked the platform to do.
          </p>
        </Section>

        <Section title="How long we keep it">
          <p>Active data persists for as long as you use the platform.</p>
          <p>When you delete your account (Settings → Account → Delete account), we immediately:</p>
          <ul className="ml-5 list-disc space-y-1">
            <li>
              delete your password, all your sessions, your AI credit ledger, your notifications,
              and your Study Room conversations
            </li>
            <li>
              scrub your name, email, and avatar from your profile (replaced with "Deleted user")
            </li>
            <li>soft-delete your course enrollments (the gradebook keeps the historical record)</li>
            <li>
              keep instructor-authored content (courses, posts, messages) but show "Deleted user" in
              place of your name, so other students don't lose their context
            </li>
          </ul>
          <p>
            Audit-log entries for security-critical events stay forever — we cannot legally delete
            those.
          </p>
        </Section>

        <Section title="Your rights">
          <p>
            <strong>Erasure:</strong> Settings → Account → Delete account. This is a self-service
            action; you don't need to email anyone.
          </p>
          <p>
            <strong>Access / portability:</strong> Settings → Account → Download my data. You'll get
            a JSON file with your profile, courses, assessment attempts, posts, sent messages, study
            sessions, and audit-log entries.
          </p>
          <p>
            <strong>Correction:</strong> you can edit your name + display name + email under
            Settings → Profile.
          </p>
        </Section>

        <Section title="Children + FERPA">
          <p>
            EduBorg is intended for use in educational settings. If your school or district has
            signed an EduBorg agreement, the school is the data controller for student records under
            FERPA; we are the processor. School-side admin contacts can request student-record
            access or deletion on a student's behalf via their EduBorg admin console.
          </p>
          <p>
            For under-13 users, parental consent must be on file with the school per COPPA before an
            account is created. Individual-teacher tier accounts must not be used to onboard
            under-13 students without that consent.
          </p>
        </Section>

        <Section title="Security">
          <p>
            Passwords are hashed with PBKDF2. Sessions are short-lived bearer tokens stored in
            HTTP-only cookies. WebSocket connections use single-use HMAC-signed tickets. All traffic
            is HTTPS-only with HSTS. Sensitive endpoints (sign-in, sign-up, change-password,
            account-delete) are rate-limited per IP.
          </p>
        </Section>

        <Section title="Contact">
          <p>
            Privacy questions or requests: <Email />.
          </p>
        </Section>
      </main>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mb-8 space-y-3 text-sm leading-relaxed text-foreground">
      <h2 className="font-display text-lg font-semibold">{title}</h2>
      {children}
    </section>
  );
}

function Email() {
  return (
    <a href="mailto:support@eduborg.com" className="font-medium underline">
      support@eduborg.com
    </a>
  );
}
